import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';
import { getIcon } from '../utils/icons-library.js';

/**
 * SERVICES — Lista de servicios con precio.
 *
 * Pensado para negocios de oficios y servicios locales (peluquerías, talleres,
 * veterinarios, pintores, fisios, estética…): cada item es un servicio con
 * icono, nombre, descripción breve y PRECIO destacado en el color de acento.
 *
 * Reutiliza el array `items: [{ name, desc, price, icon }]` — mismo patrón que
 * features/process + un campo `price`. Extracción declarativa estándar
 * (core/extractor.js: selector `.service-card`, fields name/desc/price). El
 * icono NO se edita inline (lo escribe el icon-picker directo en el store), por
 * eso el extractor lo preserva al no estar en `fields`.
 *
 * Variantes (render por rama, CSS-driven, clases `.service-*` compartidas → DRY):
 *   v1 = Tarjetas: grid de cards con icono arriba y precio destacado al pie.
 *   v2 = Lista: filas (icono + nombre + desc a la izquierda, precio a la
 *        derecha) separadas por hairline.
 */
export const servicesBlock = (rawProps, index = 0) => {
    const p = safe(rawProps);
    // `safe()` (utils/contrast.js) YA dejó `p.items` en un array, así que este `|| []` es
    // cinturón, NO un default de contenido: con 0 items este bloque se pinta VACÍO (titular +
    // contenedor sin nada, y sin ningún "+" con el que añadir desde el lienzo). De que no
    // llegue a publicarse así se encarga la guarda de CPU `_fix_bloques_sin_contenido`
    // (llm_sanitizers.py::_ARRAY_DE_CONTENIDO), que descarta el bloque al generar.
    const items = p.items || [];
    const variant = p.variant || 'v1';
    // v1: hasta 3 servicios → rejilla (.services-grid, auto-fit) que caben en fila sin recortarse;
    // con 4+ pasa a scroll horizontal (estilo pricing, flex-slider) en vez de apilar más columnas.
    // Umbral >3 (no >2) alineado con pricing: 3 cards en scroll recortaban el borde del 3º porque
    // el flex-basis 32% es proporcional (32%×3 + gaps + padding > 100%) → el grid lo evita.
    const isScroll = items.length > 3;

    // Contraste de los botones de la toolbar (idéntico a process/bento/features).
    const isDark = getLuminance(...hexToRgb(p.bgColor)) < 0.179;
    const btnBg = isDark ? "#ffffff" : "#111827";
    const btnIcon = isDark ? "#111827" : "#ffffff";

    // --- Toolbar por item (copiada de process.js → reusa handlers globales:
    //     add/borrar/drag/icon-picker, todos keyed por data-array/data-index) ---
    const itemToolbar = (i) => `
        ${i !== 0 ? `<button class="item-add-btn card-add-left" data-array="items" data-index="${i}">+</button>` : ''}
        <button class="item-add-btn card-add-right" data-array="items" data-index="${i}">+</button>
        <div class="item-tools">
            <button class="tool-btn icon-picker-btn" data-array="items" data-index="${i}" title="Cambiar icono" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.palette || '🎨'}</button>
            <button class="tool-btn item-drag-handle" title="Mover" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.drag}</button>
            <button class="tool-btn item-delete-btn" data-array="items" data-index="${i}" title="Borrar" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.trash}</button>
        </div>`;

    // Icono del servicio (fallback 'check' si el item no trae icono).
    const serviceIcon = (item, size = 24) => getIcon(item.icon || 'check', size);

    // --- Encabezado compartido ---
    const header = `
        <div class="service-head">
            ${p.title ? `<h2 data-field="title" contenteditable="true">${p.title}</h2>` : ''}
            ${p.subtitle ? `<p data-field="subtitle" contenteditable="true">${p.subtitle}</p>` : ''}
        </div>`;

    // Atributos comunes de cada item (sortable + array keys para los handlers).
    const cardAttrs = (i) => `data-item-index="${i}" data-array-name="items" data-array="items" data-index="${i}" style="--stagger-i:${i};"`;

    // ═══════════════════════════════════════════
    // v2 — Lista: filas con precio a la derecha + hairline
    // ═══════════════════════════════════════════
    if (variant === 'v2') {
        return `
    <section id="bloque-services-${index}" class="reveal variant-${variant}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding: 100px 8%;">
        ${header}
        <div class="services-list">
            ${items.map((item, i) => `
            <div class="service-card service-row sortable-item stagger-item" ${cardAttrs(i)}>
                ${itemToolbar(i)}
                <span class="service-row-icon">${serviceIcon(item, 22)}</span>
                <div class="service-row-text">
                    <h3 class="service-name" data-field="name" contenteditable="true">${item.name || ''}</h3>
                    <p class="service-desc" data-field="desc" contenteditable="true">${item.desc || ''}</p>
                </div>
                <div class="service-price" data-field="price" contenteditable="true">${item.price || ''}</div>
            </div>`).join('')}
        </div>
    </section>`;
    }

    // ═══════════════════════════════════════════
    // v1 (default) — Tarjetas: grid con icono arriba y precio destacado al pie
    // ═══════════════════════════════════════════
    return `
    <section id="bloque-services-${index}" class="reveal variant-${variant}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding: 100px 8%;">
        ${header}
        <div class="${isScroll ? 'services-scroll flex-slider' : 'services-grid'}">
            ${items.map((item, i) => `
            <div class="service-card sortable-item stagger-item" ${cardAttrs(i)}>
                ${itemToolbar(i)}
                <span class="service-icon">${serviceIcon(item, 24)}</span>
                <h3 class="service-name" data-field="name" contenteditable="true">${item.name || ''}</h3>
                <p class="service-desc" data-field="desc" contenteditable="true">${item.desc || ''}</p>
                <div class="service-price" data-field="price" contenteditable="true">${item.price || ''}</div>
            </div>`).join('')}
        </div>
    </section>`;
};
